import React, { useEffect, useState } from 'react'
import {
	Drawer, List, Rate, Spin, Input
} from 'antd'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import { getJobFinishes, IJobFinishesResponse } from '../reducers/finishes/actions'
import { translateFrequencyToSk } from '../utils/translate'
import { rateColor } from './Ratings'

type Props = {
	jobID: number
	numberOfMonths?: number
	onSelectFinish?: (id: number) => void
	selectedFinishId?: number | null
	defaultStringValue?: string
	disabled?: boolean
}

const JobFinishesDrawer: React.FC<Props> = ({
	jobID, numberOfMonths = 2, onSelectFinish, selectedFinishId, defaultStringValue, disabled
}) => {
	const { t } = useTranslation('pages')
	const dispatch = useDispatch()
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [finishes, setFinishes] = useState<IJobFinishesResponse['originalData']>([])
	const [selectedFinish, setSelectedFinish] = useState<any>(null)

	useEffect(() => {
		if (selectedFinishId && finishes.length > 0) {
			const found = finishes.find((f) => f.id === selectedFinishId)
			if (found) setSelectedFinish(found)
		}
	}, [selectedFinishId, finishes])

	useEffect(() => {
		if (open && jobID) {
			setLoading(true)
			dispatch(getJobFinishes(
				{ jobID, numberOfMonths },
				(data) => {
					setFinishes(data.originalData)
					setLoading(false)
				},
				() => {
					setFinishes([])
					setLoading(false)
				}
			))
		}
	}, [open, jobID, numberOfMonths])

	const address = finishes.length > 0 ? finishes[0]?.job?.address : ''

	const handleSelectFinish = (finish: any) => {
		setSelectedFinish(finish)
		setOpen(false)
		if (onSelectFinish) {
			onSelectFinish(finish.id)
		}
	}

	return (
		<>
			<Input
				readOnly
				disabled={disabled}
				value={
					selectedFinish
						? `${dayjs(selectedFinish.executionForDayDate).format('DD.MM.YYYY')} - ${selectedFinish.user?.firstName ?? ''} ${selectedFinish.user?.lastName ?? ''}`
						: (defaultStringValue || (t('Zobraziť dokončenia') as string))
				}
				onClick={() => setOpen(true)}
				style={{ cursor: 'pointer', backgroundColor: '#fff' }}
			/>
			<Drawer
				title={`${t('Zákazka')} #${jobID} ${address ? `- ${address}` : ''}`}
				placement="right"
				onClose={() => setOpen(false)}
				visible={open} // AntD v6 používa `open`
				width={600}
			>
				{loading ? (
					<div className="flex justify-center items-center h-full">
						<Spin />
					</div>
				) : (
					<List
						dataSource={finishes}
						renderItem={(finish: any) => (
							<List.Item
								key={finish.id}
								onClick={() => handleSelectFinish(finish)}
								style={{ cursor: 'pointer' }}
							>
								<List.Item.Meta
									title={
										<>
											{t('Dátum')}:{' '}
											{finish.executionForDayDate
												? dayjs(finish.executionForDayDate).format('DD.MM.YYYY')
												: '-'}
										</>
									}
									description={
										<div style={{ display: 'table', width: '100%' }}>
											<div style={{ display: 'table-row' }}>
												<div style={{ display: 'table-cell', fontWeight: 500, paddingRight: 12 }}>
													{t('Frekvencia')}:
												</div>
												<div style={{ display: 'table-cell' }}>
													{translateFrequencyToSk(finish?.frequency)}
												</div>
											</div>
											<div style={{ display: 'table-row' }}>
												<div style={{ display: 'table-cell', fontWeight: 500, paddingRight: 12 }}>
													{t('Užívateľ')}:
												</div>
												<div style={{ display: 'table-cell' }}>
													{finish.user?.firstName} {finish.user?.lastName}
												</div>
											</div>
											{finish.rating?.rating && finish.rating?.rating > 0 && (
												<div style={{ display: 'table-row' }}>
													<div style={{ display: 'table-cell', fontWeight: 500, paddingRight: 12 }}>
														{t('Hodnotenie')}:
													</div>
													<div style={{ display: 'table-cell' }}>
														<Rate
															count={4}
															style={{
																color: rateColor(finish.rating?.rating),
																margin: '0px 12px',
																minWidth: 112
															}}
															value={finish.rating?.rating}
															disabled
														/>
													</div>
												</div>
											)}
										</div>
									}
								/>
							</List.Item>
						)}
					/>
				)}
			</Drawer>
		</>
	)
}

export default JobFinishesDrawer
